import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiChevronDoubleRight, mdiArrowExpand, mdiClose } from "@mdi/js";
import Lottie from "lottie-react";

import { useAuth } from "../contexts/AuthContext";
import StoreBanner from "../components/store-banner";
import { useMobileMenu } from "../hooks/useMobileMenu";
import "./home.css";

import danceLocaleIcon from "../assets/dance_locale_3-200h.png";
import foxyRoxyAnimation2 from "../assets/animations/Foxy2.json";

import heroCalendarShot from "../assets/simulator_screenshot_3.png";
import heroCheckInShot from "../assets/checkin_2.png";

const HeroLightbox = ({ src, alt, onClose, triggerRef }) => {
    useEffect(() => {
        document.body.style.overflow = "hidden";
        const handleKey = (e) => { if (e.key === "Escape") handleClose(true); };
        window.addEventListener("keydown", handleKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKey);
        };
    }, [onClose]);

    const closeRef = useRef(null);
    useEffect(() => { closeRef.current?.focus(); }, []);
    const handleClose = useCallback((fromKeyboard = false) => {
        onClose();
        if (fromKeyboard) triggerRef?.current?.focus();
    }, [onClose, triggerRef]);

    return (
        <div className="home-lightbox-backdrop" onClick={handleClose} role="dialog" aria-modal="true" aria-label="Image viewer">
            <button className="home-lightbox-close" ref={closeRef} onClick={handleClose} aria-label="Close image viewer">
                <Icon path={mdiClose} size={1} color="var(--dl-color-gray-white)" />
            </button>
            <img
                src={src}
                alt={alt}
                className="home-lightbox-img"
                onClick={(e) => e.stopPropagation()}
            />
        </div>
    );
};

const Home = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [lightbox, setLightbox] = useState(null);
    const phoneRef = useRef(null);
    const ipadRef = useRef(null);
    const openLightbox = useCallback((src, alt, triggerRef) => setLightbox({ src, alt, triggerRef }), []);
    const closeLightbox = useCallback(() => setLightbox(null), []);

    useEffect(() => {
        const handleScroll = () => {
            const header = document.querySelector(".home-header-container");
            if (!header) return;
            if (window.scrollY > 50) header.classList.add("scrolled");
            else header.classList.remove("scrolled");
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleLogout = () => {
        logout();
    };

    useMobileMenu();

    return (
        <>
        <div className="home-page">
            {/* React 19 native — browser-tab UX only (does NOT reach OG crawlers on CRA SPA) */}
            <title>Dance Locale – Classes, privates, and events. One platform.</title>

            {/* Header chrome — matches LearnMore.js, active-link orange on Home */}
            <div data-role="Header" className="home-header-container">
                <header className="home-header">
                    <a href="/" className="home-logo1">
                        <img alt="logo" src={danceLocaleIcon} className="home-image1" />
                        <span className="nav-wordmark">Dance Localé</span>
                    </a>
                    <div className="home-menu1">
                        <span
                            className="home-text10"
                            style={{ color: "var(--dl-color-orange-default)", cursor: "pointer" }}>
                            Home
                        </span>
                        <a
                            href="/learn-more"
                            className="home-text11"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Learn More
                        </a>
                        <a
                            href="/how-it-works"
                            className="home-text11"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            How it Works
                        </a>
                        <a
                            href="/features"
                            className="home-text11"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Features
                        </a>
                        {user ? (
                            <>
                                {user.organisationId && (
                                    <a
                                        href="/dashboard"
                                        className="home-text12"
                                        style={{ cursor: "pointer", textDecoration: "none" }}>
                                        Dashboard
                                    </a>
                                )}
                                <a
                                    href="/my-hub"
                                    className="home-text12"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    My Hub
                                </a>
                                <button
                                    onClick={handleLogout}
                                    className="home-text13"
                                    style={{ cursor: "pointer", background: "none", border: "none", color: "inherit" }}>
                                    Logout
                                </button>
                            </>
                        ) : (
                            <>
                                <a
                                    href="/login"
                                    className="home-text12"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Login
                                </a>
                                <a
                                    href="/contact"
                                    className="home-text13"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Contact
                                </a>
                            </>
                        )}
                    </div>
                    <div className="home-container11">
                        <StoreBanner />
                    </div>
                    <div data-role="BurgerMenu" className="home-burger-menu">
                        <svg viewBox="0 0 1024 1024" className="home-menu2">
                            <path d="M810.667 725.333h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 426.667h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                            <path d="M810.667 128h-597.333c-47.061 0-85.333 38.272-85.333 85.333s38.272 85.333 85.333 85.333h597.333c47.061 0 85.333-38.272 85.333-85.333s-38.272-85.333-85.333-85.333z"></path>
                        </svg>
                    </div>
                </header>

                {/* Mobile Menu */}
                <div data-role="MobileMenu" className="home-mobile-menu">
                    <div className="home-top">
                        <div className="home-logo2">
                            <img alt="logo" src={danceLocaleIcon} className="home-image2" />
                        </div>
                        <div data-role="CloseMobileMenu" className="home-close-menu">
                            <svg viewBox="0 0 1024 1024" className="home-icon15">
                                <path d="M810 274l-238 238 238 238-60 60-238-238-238 238-60-60 238-238-238-238 60-60 238 238 238-238z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className="home-mid">
                        <span
                            className="home-text14"
                            style={{ cursor: "pointer", color: "var(--dl-color-orange-default)" }}>
                            Home
                        </span>
                        <a
                            href="/learn-more"
                            className="home-text15"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Learn More
                        </a>
                        <a
                            href="/how-it-works"
                            className="home-text15"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            How it Works
                        </a>
                        <a
                            href="/features"
                            className="home-text15"
                            style={{ cursor: "pointer", textDecoration: "none" }}>
                            Features
                        </a>
                        {user ? (
                            <>
                                {user.organisationId && (
                                    <a
                                        href="/dashboard"
                                        className="home-text16"
                                        style={{ cursor: "pointer", textDecoration: "none" }}>
                                        Dashboard
                                    </a>
                                )}
                                <a
                                    href="/my-hub"
                                    className="home-text16"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    My Hub
                                </a>
                                <span className="home-text17" onClick={handleLogout} style={{ cursor: "pointer" }}>
                                    Logout
                                </span>
                            </>
                        ) : (
                            <>
                                <a
                                    href="/login"
                                    className="home-text16"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Login
                                </a>
                                <a
                                    href="/contact"
                                    className="home-text17"
                                    style={{ cursor: "pointer", textDecoration: "none" }}>
                                    Contact
                                </a>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* HERO — full-bleed white strip on gradient page background */}
            <section className="home-strip home-strip-hero">
                <div className="home-strip-inner home-hero-inner">
                    <div className="home-hero-content">
                        <h1 className="home-hero-headline">Classes, privates, and events. One platform.</h1>
                        <p className="home-hero-subline">
                            Built for dance schools and instructors.<br />No monthly fee.
                        </p>
                        <p className="home-hero-support"><em>Use one tool, all three, or anything in between.</em></p>
                        <div className="home-hero-ctas">
                            <a href="/register" className="home-cta-primary">
                                Register Free
                            </a>
                            <a href="/learn-more" className="home-cta-learn-more-link">
                                <em>
                                    Learn More <Icon path={mdiChevronDoubleRight} size={1.05} />
                                </em>
                            </a>
                        </div>
                    </div>
                    <div className="home-hero-shot-middle">
                        <span className="home-hero-caption">Private calendar</span>
                        <div className="home-hero-img-wrap"
                            role="button" tabIndex={0} ref={phoneRef}
                            aria-label="View larger image"
                            onClick={() => openLightbox(heroCalendarShot, "Mobile app showing instructor availability with travel schedule", phoneRef)}
                            onKeyDown={(e) => e.key === "Enter" && openLightbox(heroCalendarShot, "Mobile app showing instructor availability with travel schedule", phoneRef)}>
                            <img src={heroCalendarShot} alt="Mobile app showing instructor availability with travel schedule" className="home-hero-shot" />
                            <span className="home-hero-expand home-hero-expand-br" aria-hidden="true">
                                <Icon path={mdiArrowExpand} size={0.8} color="var(--dl-color-gray-white)" />
                            </span>
                        </div>
                    </div>
                    <div className="home-hero-ipad-right">
                        <span className="home-hero-caption">Class check-in dashboard</span>
                        <div className="home-hero-img-wrap"
                            role="button" tabIndex={0} ref={ipadRef}
                            aria-label="View larger image"
                            onClick={() => openLightbox(heroCheckInShot, "Class check-in dashboard showing student roster and session stats", ipadRef)}
                            onKeyDown={(e) => e.key === "Enter" && openLightbox(heroCheckInShot, "Class check-in dashboard showing student roster and session stats", ipadRef)}>
                            <img src={heroCheckInShot} alt="Class check-in dashboard showing student roster and session stats" className="home-hero-ipad-img" />
                            <span className="home-hero-expand home-hero-expand-br" aria-hidden="true">
                                <Icon path={mdiArrowExpand} size={0.8} color="var(--dl-color-gray-white)" />
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* "Here to dance?" — full-bleed white strip on gradient page background */}
            <section className="home-strip home-strip-here-to-dance">
                <div className="home-strip-inner home-here-to-dance-inner">
                    <span className="home-here-to-dance-label">Here to dance?</span>
                    <div className="home-here-to-dance-buttons">
                        <a href="/instructors" className="home-cta-primary">
                            Find Instructors
                        </a>
                        <a href="/events" className="home-cta-tickets">
                            Event Tickets
                        </a>
                        <a href="/schools" className="home-cta-secondary">
                            Find Dance Schools
                        </a>
                    </div>
                </div>
            </section>

            {/* Bottom CTAs — full-bleed white strip on gradient page background */}
            <section className="home-strip home-strip-bottom-ctas">
                <div className="home-strip-inner home-bottom-ctas-inner">
                    <a href="/learn-more" className="home-bottom-cta-card">
                        <span className="home-bottom-cta-title">
                            Learn More <Icon path={mdiChevronDoubleRight} size={1.05} />
                        </span>
                        <span className="home-bottom-cta-blurb">
                            Pricing and how we compare to other tools.
                        </span>
                    </a>
                    <a href="/how-it-works" className="home-bottom-cta-card">
                        <span className="home-bottom-cta-title">
                            How It Works <Icon path={mdiChevronDoubleRight} size={1.05} />
                        </span>
                        <span className="home-bottom-cta-blurb">
                            Video walkthroughs and answers to common questions.
                        </span>
                    </a>
                    <a href="/features" className="home-bottom-cta-card">
                        <span className="home-bottom-cta-title">
                            Feature List <Icon path={mdiChevronDoubleRight} size={1.05} />
                        </span>
                        <span className="home-bottom-cta-blurb">
                            Everything the platform does, grouped by category.
                        </span>
                    </a>
                </div>
            </section>

            {/* Meet Foxy Roxy — full-bleed white strip on gradient page background */}
            <section className="home-strip home-strip-meet-foxy">
                <div className="home-strip-inner home-meet-foxy-inner">
                    <Lottie animationData={foxyRoxyAnimation2} loop={true} className="home-meet-foxy-lottie" />
                    <div className="home-meet-foxy-text">
                        <h3 className="home-meet-foxy-title">Meet Foxy Roxy</h3>
                        <p className="home-meet-foxy-body">
                            Your guide to the local dance scene. Foxy welcomes new dancers in the mobile app.
                        </p>
                    </div>
                </div>
            </section>

            {/* Footer — verbatim from LearnMore.js */}
            <div className="home-footer">
                <footer className="home-container22">
                    <div className="home-container23">
                        <span
                            className="home-text56"
                            onClick={() => navigate("/privacy-policy")}
                            style={{ cursor: "pointer" }}>
                            Privacy policy
                        </span>
                        <span
                            className="home-text57"
                            onClick={() => navigate("/terms-conditions")}
                            style={{ cursor: "pointer" }}>
                            Terms of Service
                        </span>
                        <span
                            className="home-text58"
                            onClick={() => navigate("/contact")}
                            style={{ cursor: "pointer" }}>
                            Contact
                        </span>
                    </div>
                    <div className="home-divider"></div>
                    <div className="home-container24">
                        <span className="home-text59 Body2">
                            © {new Date().getFullYear()} Dance Locale. All rights reserved
                        </span>
                        <div className="home-container25">
                            <a
                                href="https://www.instagram.com/dancelocalehq/"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link5">
                                <div className="home-container26">
                                    <svg viewBox="0 0 877.7142857142857 1024" className="home-icon25">
                                        <path d="M585.143 512c0-80.571-65.714-146.286-146.286-146.286s-146.286 65.714-146.286 146.286 65.714 146.286 146.286 146.286 146.286-65.714 146.286-146.286zM664 512c0 124.571-100.571 225.143-225.143 225.143s-225.143-100.571-225.143-225.143 100.571-225.143 225.143-225.143 225.143 100.571 225.143 225.143zM725.714 277.714c0 29.143-23.429 52.571-52.571 52.571s-52.571-23.429-52.571-52.571 23.429-52.571 52.571-52.571 52.571 23.429 52.571 52.571zM438.857 152c-64 0-201.143-5.143-258.857 17.714-20 8-34.857 17.714-50.286 33.143s-25.143 30.286-33.143 50.286c-22.857 57.714-17.714 194.857-17.714 258.857s-5.143 201.143 17.714 258.857c8 20 17.714 34.857 33.143 50.286s30.286 25.143 50.286 33.143c57.714 22.857 194.857 17.714 258.857 17.714s201.143 5.143 258.857-17.714c20-8 34.857-17.714 50.286-33.143s25.143-30.286 33.143-50.286c22.857-57.714 17.714-194.857 17.714-258.857s5.143-201.143-17.714-258.857c-8-20-17.714-34.857-33.143-50.286s-30.286-25.143-50.286-33.143c-57.714-22.857-194.857-17.714-258.857-17.714zM877.714 512c0 60.571 0.571 120.571-2.857 181.143-3.429 70.286-19.429 132.571-70.857 184s-113.714 67.429-184 70.857c-60.571 3.429-120.571 2.857-181.143 2.857s-120.571 0.571-181.143-2.857c-70.286-3.429-132.571-19.429-184-70.857s-67.429-113.714-70.857-184c-3.429-60.571-2.857-120.571-2.857-181.143s-0.571-120.571 2.857-181.143c3.429-70.286 19.429-132.571 70.857-184s113.714-67.429 184-70.857c60.571-3.429 120.571-2.857 181.143-2.857s120.571-0.571 181.143 2.857c70.286 3.429 132.571 19.429 184 70.857s67.429 113.714 70.857 184c3.429 60.571 2.857 120.571 2.857 181.143z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a
                                href="https://www.facebook.com/DanceLocaleHQ/"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link6">
                                <div className="home-container27">
                                    <svg viewBox="0 0 24 24" className="home-icon27">
                                        <path d="M12 2.04C6.5 2.04 2 6.53 2 12.06C2 17.06 5.66 21.21 10.44 21.96V14.96H7.9V12.06H10.44V9.85C10.44 7.34 11.93 5.96 14.22 5.96C15.31 5.96 16.45 6.15 16.45 6.15V8.62H15.19C13.95 8.62 13.56 9.39 13.56 10.18V12.06H16.34L15.89 14.96H13.56V21.96A10 10 0 0 0 22 12.06C22 6.53 17.5 2.04 12 2.04Z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a
                                href="https://x.com"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link7">
                                <div className="home-container28">
                                    <svg viewBox="0 0 950.8571428571428 1024" className="home-icon29">
                                        <path d="M925.714 233.143c-25.143 36.571-56.571 69.143-92.571 95.429 0.571 8 0.571 16 0.571 24 0 244-185.714 525.143-525.143 525.143-104.571 0-201.714-30.286-283.429-82.857 14.857 1.714 29.143 2.286 44.571 2.286 86.286 0 165.714-29.143 229.143-78.857-81.143-1.714-149.143-54.857-172.571-128 11.429 1.714 22.857 2.857 34.857 2.857 16.571 0 33.143-2.286 48.571-6.286-84.571-17.143-148-91.429-148-181.143v-2.286c24.571 13.714 53.143 22.286 83.429 23.429-49.714-33.143-82.286-89.714-82.286-153.714 0-34.286 9.143-65.714 25.143-93.143 90.857 112 227.429 185.143 380.571 193.143-2.857-13.714-4.571-28-4.571-42.286 0-101.714 82.286-184.571 184.571-184.571 53.143 0 101.143 22.286 134.857 58.286 41.714-8 81.714-23.429 117.143-44.571-13.714 42.857-42.857 78.857-81.143 101.714 37.143-4 73.143-14.286 106.286-28.571z"></path>
                                    </svg>
                                </div>
                            </a>
                            <a
                                href="http://www.youtube.com/@DanceLocale"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="home-link8">
                                <div className="home-container29">
                                    <svg viewBox="0 0 1024 1024" className="home-icon31">
                                        <path d="M406.286 644.571l276.571-142.857-276.571-144.571v287.429zM512 152c215.429 0 358.286 10.286 358.286 10.286 20 2.286 64 2.286 102.857 43.429 0 0 31.429 30.857 40.571 101.714 10.857 82.857 10.286 165.714 10.286 165.714v77.714s0.571 82.857-10.286 165.714c-9.143 70.286-40.571 101.714-40.571 101.714-38.857 40.571-82.857 40.571-102.857 42.857 0 0-142.857 10.857-358.286 10.857v0c-266.286-2.286-348-10.286-348-10.286-22.857-4-74.286-2.857-113.143-43.429 0 0-31.429-31.429-40.571-101.714-10.857-82.857-10.286-165.714-10.286-165.714v-77.714s-0.571-82.857-0.571-165.714c9.143-70.857 40.571-101.714 40.571-101.714 38.857-41.143 82.857-41.143 102.857-43.429 0 0 142.857-10.286 358.286-10.286v0z"></path>
                                    </svg>
                                </div>
                            </a>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
        {lightbox && (
            <HeroLightbox
                src={lightbox.src}
                alt={lightbox.alt}
                triggerRef={lightbox.triggerRef}
                onClose={closeLightbox}
            />
        )}
        </>
    );
};

export default Home;
